import React, {useState} from 'react';
import { Button, DatePicker, Form, Input, message, Modal, Popconfirm, Select, Space, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import moment from 'moment';
import {PREFERRED_DATE_FORMAT} from '../util/time';
import {deleteScheduled, updateScheduled} from '../api/scheduledApi';
import { PSA_INSTANCE } from './Constants';

const PTO_POLICY_OPTIONS = [
    {label: "Standard", value: "Standard"},
    {label: "Hours Cap", value: "Hours Cap"},
    {label: "Multiplier Cap", value: "Multiplier Cap"},
    {label: "No Accrual", value: "No Accrual"},
];

const SCHEDULED_COLUMNS = [
    'email',
    'prev_email',
    'first_name',
    'last_name',
    'title',
    'level',
    'contractor_rate',
    'practice_name',
    'location_name',
    'capability',
    'career_coach_id',
    'manager_id',
    'start_date',
    'term_date',
    'employment_status',
    'pto_policy',
    'pto_modifier',
    'pto_hours_override',
    'psa_instances',
    'primary_psa_instance'
];

const ScheduledEditButton = ({record, onRefresh, onRecordUpdated,manageData}) => {
    const handleColumnChange = React.useCallback((column) => {
        switch(column) {
            case "level":
                setOptions(manageData.levels.map(level => ({label: String(level), value: String(level)})));
                break;
            case "capability":
                setOptions(manageData.capabilities.map(capability => ({label: capability, value: capability})));
                break;
            case "employment_status":
                setOptions(manageData.employment_status.map(status => ({label: status, value: status})));
                break;
            case "pto_policy":
                setOptions(PTO_POLICY_OPTIONS);
                break;
            case "practice_name":
                setOptions(manageData.practices.map(practice => ({label: practice, value: practice})));
                break;
            case "location_name":
                setOptions(manageData.locations.map(location => ({label: location, value: location})));
                break;
            case "title":
                setOptions(manageData.titles.map(title => ({label: title, value: title})));
                break;
            case "manager_id":
                setOptions(manageData.managers.map(managers => ({label: managers.e, value: managers.e})));
                break;
            case "career_coach_id":
                setOptions(manageData.managers.map(managers => ({label: managers.e, value: managers.e})));
                break;
            case "psa_instances":
                setOptions(PSA_INSTANCE);
                break;
            case "primary_psa_instance":
                setOptions(PSA_INSTANCE);
                break;
            default:
                setOptions(manageData.capabilities.map(capability => ({label: capability, value: capability})));
                break;
        }
    },[manageData]);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const [options, setOptions] = useState([]);
    const [selectedColumn, setSelectedColumn] = useState("");
React.useEffect(() => {
    if (editModalVisible && record?.column) {
        setSelectedColumn(record.column);
        handleColumnChange(record.column);
    }
}, [editModalVisible, record?.column, handleColumnChange]);
    const isDropdownColumn = [
        "title",
        "level",
        "practice_name",
        "location_name",
        "capability",
        "location",
        "career_coach_id",
        "manager_id",
        "employment_status",
        "pto_policy",
        "primary_psa_instance"
    ].includes(selectedColumn);

    const isMultipleSelection = [
        "psa_instances"
    ].includes(selectedColumn);

    const isDateColumn =
      ["start_date", "term_date"].includes(selectedColumn);

    const isNumberColumn =
      ["pto_modifier", "pto_policy"].includes(selectedColumn);

    const isContractorRate =
      ["contractor_rate"].includes(selectedColumn);


    const isEditable =
        record.status === "scheduled" &&
        (record.processed_at === null || record.processed_at === "" || record.processed_at === undefined);

    const handleEditClick = () => {
        form.setFieldsValue({
            effective_date: moment(record.effective_date).utc(false),
            column: record.column,
            value:
              record.column === "psa_instances"
                ? (record.value ? record.value.split(",") : [])
                : record.value,
        });
        setEditModalVisible(true);
    };

    const handleEditSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return; // validation errors shown inline
        }
        try {
            setSaving(true);
            const updatedFields = {
                effective_date: values.effective_date.format('YYYY-MM-DD'),
                column: values.column,
                value: Array.isArray(values.value)
                  ? values.value.join(",")
                  : values.value ?? "",
            };
            await updateScheduled(record.ID, updatedFields);
            message.success("Record updated successfully");
            setEditModalVisible(false);
            form.resetFields();
            onRecordUpdated(record.ID, updatedFields);
        } catch {
            message.error("Failed to update record");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteClick = async () => {
        try {
            await deleteScheduled(record.ID);
            message.success("Record deleted successfully");
            onRefresh();
        } catch {
            message.error("Failed to delete record");
        }
    };

    return (
        <>
            <Space size="small">
                <Tooltip title={
                    isEditable
                        ? "Edit"
                        : "Edit is only available for scheduled records that have not been processed"
                }>
                    <Button
                        icon={<EditOutlined/>}
                        disabled={!isEditable}
                        onClick={handleEditClick}
                    />
                </Tooltip>
                <Tooltip title="Remove">
                    <Popconfirm
                        title="Are you sure to remove this record (this cannot be undone)?"
                        onConfirm={handleDeleteClick}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button icon={<DeleteOutlined/>}/>
                    </Popconfirm>
                </Tooltip>
            </Space>

            <Modal
                title="Edit Scheduled Record"
                open={editModalVisible}
                onOk={handleEditSubmit}
                onCancel={() => {
                    setEditModalVisible(false);
                    form.resetFields();
                }}
                okText="Save"
                cancelText="Cancel"
                confirmLoading={saving}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    {/* Read-only reference fields */}
                    <Form.Item label="Name">
                        <Input value={record.employee.fullName} disabled/>
                    </Form.Item>
                    <Form.Item label="Email">
                        <Input value={record.employee.email} disabled/>
                    </Form.Item>
                    <Form.Item label="Previous Value">
                        <Input value={record.previous ?? ""} disabled/>
                    </Form.Item>

                    {/* Editable fields */}
                    <Form.Item
                        name="effective_date"
                        label="Effective Date"
                        rules={[{required: true, message: 'Please select an effective date'}]}
                    >
                        <DatePicker style={{width: '100%'}} format={PREFERRED_DATE_FORMAT}/>
                    </Form.Item>
                    <Form.Item
                        name="column"
                        label="Column"
                        rules={[{required: true, message: 'Please select a column'}]}
                    >
                        <Select onChange={(value) => {
                            form.setFieldsValue({column: value, value: undefined});
                            setSelectedColumn(value);
                            handleColumnChange(value);
                        }}>
                            {SCHEDULED_COLUMNS.map(c => (
                                <Select.Option key={c} value={c}>
                                    {c}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                        {selectedColumn === "email" || selectedColumn ==="prev_email" ?(
                          <Form.Item
                            name="value"
                            label="value"
                            rules={[
                                {
                                    type: "email",
                                    message: "Please select a valid email address",
                                },
                                {
                                    required: true,
                                    message: "Please enter an email address",
                                },
                                ]}
                            >
                              <Input placeholder="Enter email address" />
                          </Form.Item>
                        ): isDropdownColumn ? (
                          <Form.Item name="value" label="Value">
                              <Select
                                placeholder="Please select a value"
                                options={options} />
                          </Form.Item>
                          ): isDateColumn ? (
                            <Form.Item name="value" label="Value">
                                <DatePicker style={{width: '100%'}} />
                            </Form.Item>
                        ): isNumberColumn ? (
                          <Form.Item name="value" label="Value">
                              <Input type="number" />
                          </Form.Item>
                        ): isContractorRate ? (
                          <Form.Item name="value" label="Value">
                              <Input type="number"
                              prefix="$" />
                          </Form.Item>
                        ) : isMultipleSelection ? (
                          <Form.Item name="value" label="Value">
                              <Select
                                placeholder="Please select one or more values"
                                options={options}
                                mode="multiple" />
                          </Form.Item>

                        ): (
                          <Form.Item name="value" label="Value">
                              <Input placeholder="Enter value" />
                          </Form.Item>
                        )}
                </Form>
            </Modal>
        </>
    );
};

export {ScheduledEditButton};